// src/pages/lkp/LookupEntityWorkspace.tsx
//
// Step 3 — FINALIZE. The entity is the spine; here the SME finalizes:
//   • the entity name (final)
//   • the binding fields (which physical fields belong to the entity) + provenance
//   • the canonical value set + source→canonical crosswalk (via EntityEditor)
//
// Cross-certification with Steps 1 & 2 is shown per binding. A binding that appears
// here but was NOT accepted in Step 1 or confirmed in Step 2 is flagged "needs
// attention" with a jump back to the right step. Re-running Discovery/Reference MERGES
// (bindings + source evidence) and never overwrites canonical/crosswalk the SME authored.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  lookup_getEntities, lookup_getEntity, lookup_rebuildEntities, lookup_foldAuthoritative,
  lookup_entityAction,
  type Buckets, type EntitiesResponse, type EntityActionName,
} from "../api/lkpEntityAPI";
import { lookup_getCandidates, lookup_getAuthoritative } from "../api/lkpAPI";
import EntityHarmonizePanel from "./EntityHarmonizePanel";
import EntityEditor from "./EntityEditor";
import { STATUS_META, CSS, valueOverlap } from "./entityShared";

interface Props { appNm: string; entityId: string; entityLabel?: string; onGoStep?: (n: 1 | 2) => void; }
export type BindingCert = { s1: boolean; s2: boolean; certified: boolean; s1status?: string; state?: "accepted" | "rejected" | "pending" };
const up = (s?: string) => (s || "").toUpperCase();
const STATUS_DESC: Record<string, string> = {
  discovered: "Proposed by Step 1, not yet accepted.",
  sourced: "Has source evidence (data and/or client) — canonical set not finalized yet.",
  harmonized: "Canonical set authored and the crosswalk covers all source values.",
  published: "Governed set published for the rule engine.",
};

export default function LookupEntityWorkspace({ appNm, entityId, entityLabel, onGoStep }: Props) {
  const [data, setData] = useState<EntitiesResponse | null>(null);
  const [selId, setSelId] = useState<string | null>(null);
  const [buckets, setBuckets] = useState<Buckets | null>(null);
  const [cert, setCert] = useState<Map<string, BindingCert>>(new Map());
  const [showHarmonize, setShowHarmonize] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ m: string; e?: boolean } | null>(null);
  const [q, setQ] = useState("");
  const [view, setView] = useState<"all" | "ready" | "attention" | "needsvalues" | "published" | "profile" | "client" | "both" | "aligned" | "clientvals" | "datavals" | "novalues">("all");
  const note = (m: string, e = false) => { setFlash({ m, e }); setTimeout(() => setFlash(null), 3500); };

  // entities + Step-1 candidates + Step-2 authoritative → per-binding certification
  const load = useCallback(async () => {
    try {
      const [r, c, a] = await Promise.allSettled([
        lookup_getEntities(entityId), lookup_getCandidates(entityId), lookup_getAuthoritative(entityId),
      ]);
      const ents = r.status === "fulfilled" ? r.value : null;
      setData(ents);
      if (ents && !selId && ents.entities.length) setSelId(ents.entities[0].entity_id);

      const s1 = new Map<string, string>();               // fieldKey -> Step-1 status
      if (c.status === "fulfilled") (c.value.data?.items || []).forEach((it: any) => {
        const ref = it.family === "lookup_field" ? it.field : it.family === "code_description_pair" ? it.code : null;
        if (ref) s1.set(up(`${ref.table}.${ref.field}`), it.status || "PROPOSED");
      });
      const s2 = new Map<string, boolean>();               // fieldKey -> Step-2 confirmed
      if (a.status === "fulfilled") (a.value.data?.definitions || []).forEach((d: any) => {
        if (d.logical_field) s2.set(up(`${d.table}.${d.column}`), !!(d.review && d.review.confirmed));
      });
      const m = new Map<string, BindingCert>();
      const keys = new Set<string>([...s1.keys(), ...s2.keys()]);
      (ents?.entities || []).forEach((e) => e.bindings.forEach((b) => keys.add(up(b.field_id))));
      keys.forEach((k) => {
        const a1 = s1.get(k) === "ACCEPTED", a2 = s2.get(k) === true;
        const certified = a1 || a2;
        const rejected = !certified && s1.get(k) === "REJECTED";
        const state: "accepted" | "rejected" | "pending" = certified ? "accepted" : rejected ? "rejected" : "pending";
        m.set(k, { s1: a1, s2: a2, certified, s1status: s1.get(k), state });
      });
      setCert(m);
    } catch { setData(null); }
  }, [entityId, selId]);
  useEffect(() => { load(); }, [entityId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadBuckets = useCallback(async (leid: string) => {
    try { const r = await lookup_getEntity(entityId, leid); setBuckets(r.buckets); } catch { setBuckets(null); }
  }, [entityId]);
  useEffect(() => { if (selId) loadBuckets(selId); }, [selId, loadBuckets, data]);

  const entities = data?.entities || [];
  const selected = entities.find((e) => e.entity_id === selId) || null;

  const entityCert = useCallback((e: any) => {
    const items = (e.bindings || []).map((b: any) => cert.get(up(b.field_id)) || { s1: false, s2: false, certified: false, state: "pending" as const });
    const uncert = items.filter((x: BindingCert) => !x.certified).length;
    const rejected = items.filter((x: BindingCert) => x.state === "rejected").length;
    const pending = items.filter((x: BindingCert) => x.state === "pending").length;
    const s1any = items.some((x: BindingCert) => x.s1); const s2any = items.some((x: BindingCert) => x.s2);
    return { total: items.length, certified: items.length - uncert, uncertified: uncert, pending, rejected, s1any, s2any };
  }, [cert]);
  const attentionTotal = useMemo(() => entities.reduce((n, e) => n + (entityCert(e).uncertified > 0 ? 1 : 0), 0), [entities, entityCert]);
  const readyNoCanon = useCallback((e: any) => entityCert(e).uncertified === 0 && e.status !== "published" && (e.canonical_values?.length ?? 0) === 0, [entityCert]);
  const needsValuesTotal = useMemo(() => entities.reduce((n, e) => n + (readyNoCanon(e) ? 1 : 0), 0), [entities, readyNoCanon]);
  const originOf = (e: any) => {
    const v = valueOverlap(e);                       // by VALUES (data vs client), not bindings
    return {
      profileOnly: v.prof > 0 && v.cli === 0,
      clientOnly: v.cli > 0 && v.prof === 0,
      both: v.prof > 0 && v.cli > 0,
      none: v.prof === 0 && v.cli === 0,
    };
  };

  const visible = entities.filter((e) => {
    const ec = entityCert(e);
    if (view === "ready" && (ec.uncertified > 0 || e.status === "published")) return false;
    if (view === "attention" && ec.uncertified === 0) return false;
    if (view === "needsvalues" && !readyNoCanon(e)) return false;
    if (view === "published" && e.status !== "published") return false;
    if (view === "profile" || view === "client" || view === "both" || view === "novalues") {
      const o = originOf(e);
      if (view === "profile" && !o.profileOnly) return false;
      if (view === "client" && !o.clientOnly) return false;
      if (view === "both" && !o.both) return false;
      if (view === "novalues" && !o.none) return false;
    }
    if (view === "aligned" || view === "clientvals" || view === "datavals") {
      const v = valueOverlap(e);
      if (view === "aligned" && !(v.comparable && v.clientOnly === 0 && v.profileOnly === 0)) return false;
      if (view === "clientvals" && !(v.comparable && v.clientOnly > 0)) return false;   // client has, data doesn't
      if (view === "datavals" && !(v.comparable && v.profileOnly > 0)) return false;    // data has, client doesn't
    }
    if (q) { const s = q.toLowerCase(); return e.name.toLowerCase().includes(s) || e.bindings.some((b: any) => b.field_id.toLowerCase().includes(s)); }
    return true;
  });

  const act = async (action: EntityActionName, payload: Record<string, any> = {}, tag: string = action) => {
    if (!selId && action !== "create") return;
    setBusy(tag);
    try {
      const r = await lookup_entityAction(entityId, action, { entity_id: selId || undefined, payload });
      await load(); if (r.buckets) setBuckets(r.buckets); else if (selId) await loadBuckets(selId);
      note("Saved.");
    } catch (e: any) { note(e?.detail || "Action failed", true); } finally { setBusy(null); }
  };

  const VIEWS: Array<{ k: typeof view; label: string; n?: number }> = [
    { k: "all", label: "All", n: entities.length },
    { k: "ready", label: "Ready", n: entities.filter((e) => entityCert(e).uncertified === 0 && e.status !== "published").length },
    { k: "attention", label: "Needs attention", n: attentionTotal },
    { k: "needsvalues", label: "Ready · needs values", n: needsValuesTotal },
    { k: "published", label: "Published", n: entities.filter((e) => e.status === "published").length },
    { k: "profile", label: "Profile only", n: entities.filter((e) => originOf(e).profileOnly).length },
    { k: "client", label: "Client only", n: entities.filter((e) => originOf(e).clientOnly).length },
    { k: "both", label: "Both sources", n: entities.filter((e) => originOf(e).both).length },
    { k: "novalues", label: "No values yet", n: entities.filter((e) => originOf(e).none).length },
    { k: "aligned", label: "Values aligned", n: entities.filter((e) => { const v = valueOverlap(e); return v.comparable && v.clientOnly === 0 && v.profileOnly === 0; }).length },
    { k: "clientvals", label: "Client-only values", n: entities.filter((e) => { const v = valueOverlap(e); return v.comparable && v.clientOnly > 0; }).length },
    { k: "datavals", label: "Data-only values", n: entities.filter((e) => { const v = valueOverlap(e); return v.comparable && v.profileOnly > 0; }).length },
  ];

  return (
    <div className="lew">
      <style>{CSS}</style>
      <style>{EXTRA}</style>
      <div className="lew-tools">
        <div className="sp" />
        <button className="btn ghost" disabled={!!busy} onClick={() => load()}>{busy ? "…" : "Refresh"}</button>
        <button className="btn ghost" disabled={!!busy} onClick={() => setShowHarmonize(true)}>Harmonize names</button>
      </div>

      {flash && <div className={"flash " + (flash.e ? "err" : "ok")}>{flash.m}</div>}
      {attentionTotal > 0 && (
        <div className="lew-attn">
          <b>{attentionTotal}</b> entit{attentionTotal === 1 ? "y" : "ies"} have binding fields not yet accepted in Step 1 or confirmed in Step 2.
          Resolve them there, then Refresh. {onGoStep && <><button className="lew-attn-b" onClick={() => onGoStep(1)}>Go to Step 1</button><button className="lew-attn-b" onClick={() => onGoStep(2)}>Go to Step 2</button></>}
        </div>
      )}
      {needsValuesTotal > 0 && (
        <div className="lew-nv">
          <b>{needsValuesTotal}</b> entit{needsValuesTotal === 1 ? "y is" : "ies are"} Ready but ha{needsValuesTotal === 1 ? "s" : "ve"} no canonical values yet — open each and use <b>Promote</b> / <b>Promote all</b> (or Add) to build the governed set.
          <button className="lew-nv-b" onClick={() => setView("needsvalues")}>Show these</button>
        </div>
      )}
      {data?.summary && (
        <div className="summary">
          <b>{data.summary.entities}</b> entities · <b>{data.summary.bound_fields}</b> bound fields ·
          <span className="warn"> {data.summary.unmapped_source_values}</span> unmapped source values
          {data.authoritative_proposals?.length ? <span className="prop"> · {data.authoritative_proposals.length} mapping proposal(s)</span> : null}
        </div>
      )}

      <div className="lew-body">
        <div className="lew-list">
          <div className="lew-search"><input placeholder="Search entities / fields…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
          <div className="lew-filters">
            {VIEWS.map((v) => (
              <button key={v.k} className={"pill " + (view === v.k ? "on" : "") + (v.k === "attention" && (v.n || 0) > 0 ? " attn" : "")} onClick={() => setView(v.k)}>{v.label}{v.n != null ? ` (${v.n})` : ""}</button>
            ))}
          </div>
          <button className="lew-new" onClick={() => { const n = prompt("New lookup entity name (e.g. Country Lookup)"); if (n) act("create", { name: n }, "create"); }}>+ New entity</button>
          <div className="lew-items">
            {visible.map((e) => {
              const sm = STATUS_META[e.status] || STATUS_META.discovered; const ec = entityCert(e);
              return (
                <button key={e.entity_id} className={"lew-item " + (selId === e.entity_id ? "on" : "")} onClick={() => setSelId(e.entity_id)}>
                  <div className="li-top"><span className="li-name">{e.name}</span>
                    <span className="li-status" style={{ background: sm.bg, color: sm.c }} title={STATUS_DESC[e.status] || ""}>{sm.t}</span></div>
                  <div className="li-meta">
                    <span>{e.bindings.length} field{e.bindings.length === 1 ? "" : "s"}</span>
                    <span>· {e.buckets?.canonical ?? e.canonical_values.length} canonical</span>
                    {ec.uncertified > 0
                      ? <>
                          {ec.pending > 0 && <span className="li-attn">· ⚠ {ec.pending} pending</span>}
                          {ec.rejected > 0 && <span className="li-rej">· ✕ {ec.rejected} rejected</span>}
                        </>
                      : <span className={"li-cert " + (ec.s1any && ec.s2any ? "both" : ec.s1any ? "s1" : "s2")}>· ✓ {ec.s1any && ec.s2any ? "Step 1 & 2" : ec.s1any ? "Step 1" : "Step 2"}</span>}
                    {readyNoCanon(e) && <span className="li-nocanon">· ⚠ no canonical</span>}
                  </div>
                  {(() => { const v = valueOverlap(e); return v.comparable ? (
                    <div className="li-vov">≡ {v.match} match{v.clientOnly ? <span className="li-vc"> · +{v.clientOnly} client-only</span> : null}{v.profileOnly ? <span className="li-vp"> · +{v.profileOnly} data-only</span> : null}</div>
                  ) : null; })()}
                </button>
              );
            })}
            {visible.length === 0 && <div className="lew-empty">No entities in this view.</div>}
          </div>
        </div>

        <div className="lew-editor">
          {!selected ? <div className="lew-empty big">Select or create a lookup entity.</div> : (
            <EntityEditor entity={selected} buckets={buckets} busy={busy} act={act} allEntities={entities}
              proposals={data?.authoritative_proposals || []} cert={cert} onGoStep={onGoStep} />
          )}
        </div>
      </div>
      {showHarmonize && <EntityHarmonizePanel entityId={entityId} onApplied={load} onClose={() => setShowHarmonize(false)} />}
    </div>
  );
}

const EXTRA = `
.lew-tools{display:flex;align-items:center;gap:8px;padding:2px 2px 12px}
.lew-attn{display:flex;align-items:center;gap:9px;flex-wrap:wrap;font-size:12.5px;color:#7c5310;background:var(--wait-bg);border:1px solid #eBd6a8;border-left:3px solid var(--wait);padding:9px 14px}
.lew-attn b{color:#5c3d0b}
.lew-attn-b{border:1px solid var(--wait);background:#fff;color:#7c5310;border-radius:6px;padding:3px 10px;font-size:11.5px;cursor:pointer;font-family:inherit}
.lew-attn-b:hover{background:var(--wait-bg)}
.lew .pill.attn{border-color:var(--wait);color:var(--wait)}
.li-rej{color:#991b1b;font-weight:600}
.li-attn{color:var(--warn);font-weight:600}.li-ok{color:var(--gov)}
.li-cert{font-weight:600}.li-cert.both{color:var(--gov)}.li-cert.s1{color:var(--infer)}.li-cert.s2{color:#0f766e}
.li-nocanon{color:var(--wait);font-weight:600}
.lew-nv{display:flex;align-items:center;gap:10px;flex-wrap:wrap;font-size:12.5px;color:#7c5310;background:var(--wait-bg);border:1px solid #eBd6a8;border-left:3px solid var(--wait);padding:9px 14px}
.lew-nv b{color:#5c3d0b}
.lew-nv-b{border:1px solid var(--wait);background:#fff;color:#7c5310;border-radius:6px;padding:3px 10px;font-size:11.5px;cursor:pointer;font-family:inherit}
.lew-nv-b:hover{background:var(--wait-bg)}
.li-vov{font-size:10.5px;color:var(--tx3);margin-top:2px}
.li-vc{color:var(--wait)}.li-vp{color:var(--warn)}
`;
